import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Lock, AlertTriangle, CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ChangePassword = () => {
  const navigate = useNavigate();
  const { user, logout, fetchUser } = useAuth();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const togglePassword = (field) => {
    setShowPasswords({ ...showPasswords, [field]: !showPasswords[field] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (formData.newPassword.length < 6) {
      toast.error('Le nouveau mot de passe doit contenir au moins 6 caractères');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (formData.currentPassword === formData.newPassword) {
      toast.error('Le nouveau mot de passe doit être différent du mot de passe actuel');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/change-password`, {
        current_password: formData.currentPassword,
        new_password: formData.newPassword
      });

      toast.success('Mot de passe modifié avec succès !');
      
      // Refresh user data to update password_temporary flag
      await fetchUser();
      
      // Redirect to guide for first login experience
      navigate('/guide', { replace: true, state: { firstLogin: true } });
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors du changement de mot de passe';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const isPasswordExpired = user?.password_expired;

  return (
    <div className="min-h-screen bg-[#FDFDFC] flex">
      {/* Left side - Info */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-[#2E4A3D] to-[#1a2d24]">
        <div className="absolute inset-0 z-20 flex flex-col justify-center p-12">
          <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mb-6">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-serif font-bold text-white mb-4">
            Sécurisez votre compte
          </h1>
          <p className="text-xl text-white/80 max-w-md mb-8">
            Pour des raisons de sécurité, vous devez créer un nouveau mot de passe personnel
          </p>
          <div className="space-y-4 text-white/70">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span>Minimum 6 caractères</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span>Différent du mot de passe temporaire</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span>Gardez-le confidentiel</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Warning Banner */}
          <div className={`rounded-lg p-4 mb-8 ${isPasswordExpired ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'}`}>
            <div className="flex items-start gap-3">
              <AlertTriangle className={`w-5 h-5 mt-0.5 ${isPasswordExpired ? 'text-red-600' : 'text-amber-600'}`} />
              <div>
                <p className={`font-medium ${isPasswordExpired ? 'text-red-800' : 'text-amber-800'}`}>
                  {isPasswordExpired 
                    ? 'Mot de passe temporaire expiré' 
                    : 'Changement de mot de passe requis'}
                </p>
                <p className={`text-sm mt-1 ${isPasswordExpired ? 'text-red-700' : 'text-amber-700'}`}>
                  {isPasswordExpired
                    ? 'Votre mot de passe temporaire a expiré. Veuillez contacter un instructeur pour en obtenir un nouveau.'
                    : 'Vous utilisez un mot de passe temporaire. Veuillez créer votre mot de passe personnel pour continuer.'}
                </p>
              </div>
            </div>
          </div>

          {isPasswordExpired ? (
            // Expired password - show contact message
            <div className="text-center">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Lock className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="text-2xl font-serif font-bold text-[#2E4A3D] mb-4">
                Session expirée
              </h2>
              <p className="text-stone-600 mb-6">
                Contactez un instructeur de votre club pour obtenir un nouveau mot de passe temporaire.
              </p>
              <Button
                onClick={handleLogout}
                className="btn-primary w-full py-3"
                data-testid="logout-expired"
              >
                Retour à la connexion
              </Button>
            </div>
          ) : (
            // Normal flow - password change form
            <>
              <div className="mb-8">
                <h2 className="text-3xl font-serif font-bold text-[#2E4A3D]">
                  Nouveau mot de passe
                </h2>
                <p className="text-stone-600 mt-2">
                  Bonjour {user?.prenom} ! Créez votre mot de passe personnel.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="currentPassword" className="form-label">
                    Mot de passe temporaire
                  </Label>
                  <div className="relative">
                    <Input
                      id="currentPassword"
                      name="currentPassword"
                      type={showPasswords.current ? 'text' : 'password'}
                      value={formData.currentPassword}
                      onChange={handleChange}
                      placeholder="Entrez le mot de passe reçu"
                      className="input-field pr-12"
                      data-testid="current-password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => togglePassword('current')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                    >
                      {showPasswords.current ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="newPassword" className="form-label">
                    Nouveau mot de passe
                  </Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      name="newPassword"
                      type={showPasswords.new ? 'text' : 'password'}
                      value={formData.newPassword}
                      onChange={handleChange}
                      placeholder="Minimum 6 caractères"
                      className="input-field pr-12"
                      data-testid="new-password"
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => togglePassword('new')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                    >
                      {showPasswords.new ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="form-label">
                    Confirmer le nouveau mot de passe
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showPasswords.confirm ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Confirmez votre mot de passe"
                      className="input-field pr-12"
                      data-testid="confirm-password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => togglePassword('confirm')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                    >
                      {showPasswords.confirm ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {formData.newPassword && formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
                    <p className="text-sm text-red-500 mt-1">Les mots de passe ne correspondent pas</p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="btn-primary w-full py-3"
                  disabled={loading}
                  data-testid="change-password-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Modification en cours...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Valider mon mot de passe
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t border-stone-200">
                <button
                  onClick={handleLogout}
                  className="text-sm text-stone-500 hover:text-stone-700 w-full text-center"
                  data-testid="logout-link"
                >
                  Se déconnecter et revenir plus tard
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
